/**
 * OfflineBanner — subtle top-of-app notice when the device is offline.
 *
 * Store-compliance requirement: apps must degrade gracefully with no
 * connection ("no crashes, friendly offline messaging" — Apple Review
 * Guidelines 2.1, Google Play crash-free UX). This banner:
 *
 *   • Listens to `online` / `offline` events (works on both web and
 *     Capacitor WebViews without needing the Capacitor Network plugin).
 *   • Renders a non-blocking pill at the top so users always know why
 *     something can't save. Auto-hides when the connection returns and
 *     shows a brief "Back online" confirmation.
 *   • Sits above the safe-area (`env(safe-area-inset-top)`) so it never
 *     overlaps the Dynamic Island / notch.
 *   • Zero UI on web when online — no impact on `app.futureself.es`.
 */

import React, { useEffect, useState } from 'react';
import { WifiOff, Wifi } from 'lucide-react';

const OfflineBanner = () => {
  const [online, setOnline] = useState(
    typeof navigator === 'undefined' ? true : navigator.onLine
  );
  const [flashBackOnline, setFlashBackOnline] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    let flashTimer;
    const onOnline = () => {
      setOnline(true);
      setFlashBackOnline(true);
      flashTimer = setTimeout(() => setFlashBackOnline(false), 2500);
    };
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      if (flashTimer) clearTimeout(flashTimer);
    };
  }, []);

  if (online && !flashBackOnline) return null;

  const isOffline = !online;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid={isOffline ? 'offline-banner' : 'online-banner'}
      className="fixed left-0 right-0 z-[200] flex justify-center pointer-events-none"
      style={{ top: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
    >
      <div
        className={`pointer-events-auto flex items-center gap-2 rounded-full px-4 py-2 text-xs font-sans backdrop-blur-md shadow-lg border ${
          isOffline
            ? 'bg-[#3E3E42]/90 text-[#DCD3CA] border-white/10'
            : 'bg-[#8BAA9E]/95 text-[#1C1C1E] border-[#8BAA9E]/30'
        }`}
      >
        {isOffline ? (
          <>
            <WifiOff className="w-3.5 h-3.5" strokeWidth={1.75} />
            <span>You're offline — some things may not save until you reconnect.</span>
          </>
        ) : (
          <>
            <Wifi className="w-3.5 h-3.5" strokeWidth={1.75} />
            <span>Back online.</span>
          </>
        )}
      </div>
    </div>
  );
};

export default OfflineBanner;
